import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { Copy, Download, Eye, Zap } from "lucide-react";
import { API_BASE, ICON_CONFIG } from "../config";

const mediaRegex = /(https?:\/\/[^\s<>()]+?\.(?:png|jpg|jpeg|gif|webp|mp4|mov)(?:\?[^\s<>()]*)?)|(\/opt\/key_college\/temp_images\/[^\s<>()]+?\.(?:png|jpg|jpeg|gif|webp|mp4|mov))/gi;

export const normalizeContent = (content = "") => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = content.replace(/\\n/g, "\n");
  return textarea.value;
};

export const getMediaUrls = (content = "") =>
  [...new Set(content.match(mediaRegex) || [])].map((url) => url.replace(/[),.;!?，。；！？、]+$/u, ""));

const getFullUrl = (path) =>
  path.startsWith("http") ? path : `${API_BASE}/temp_images/${path.split("/").pop()}`;

const typewriterStep = (length) => {
  if (length > 1600) return 18;
  if (length > 900) return 12;
  if (length > 360) return 8;
  return 4;
};

const typewriterDelay = (length) => {
  if (length > 1600) return 10;
  if (length > 900) return 12;
  return 16;
};

const downloadMedia = async (url) => {
  const filename = `LumaNova_${Date.now()}.${url.split(".").pop()?.split("?")[0] || "png"}`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error("download failed");
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

export default function MessageItem({ msg, isDark, animate = false, onAnimationDone, onAnimationStep }) {
  const isUser = msg.role === "user";
  const rawContent = normalizeContent(msg.content || "");
  const [visibleLength, setVisibleLength] = useState(animate && !isUser ? 0 : rawContent.length);
  const doneRef = useRef(onAnimationDone);
  const stepRef = useRef(onAnimationStep);
  const tickRef = useRef(0);
  const displayContent = useMemo(
    () => (animate && !isUser ? rawContent.slice(0, visibleLength) : rawContent),
    [animate, isUser, rawContent, visibleLength]
  );
  const mediaUrls = getMediaUrls(displayContent);
  const cleanContent = displayContent.replace(mediaRegex, "").trim() || displayContent;
  const botAvatar = isDark ? ICON_CONFIG.botAvatarDark : ICON_CONFIG.botAvatarLight;

  useEffect(() => {
    doneRef.current = onAnimationDone;
  }, [onAnimationDone]);

  useEffect(() => {
    stepRef.current = onAnimationStep;
  }, [onAnimationStep]);

  useEffect(() => {
    if (!animate || isUser) {
      setVisibleLength(rawContent.length);
      return undefined;
    }
    setVisibleLength(0);
    tickRef.current = 0;
    const step = typewriterStep(rawContent.length);
    const delay = typewriterDelay(rawContent.length);
    const timer = window.setInterval(() => {
      setVisibleLength((current) => {
        const next = Math.min(current + step, rawContent.length);
        tickRef.current += 1;
        if (tickRef.current % 4 === 0 || next >= rawContent.length) {
          window.setTimeout(() => stepRef.current?.(), 0);
        }
        if (next >= rawContent.length) {
          window.clearInterval(timer);
          window.setTimeout(() => doneRef.current?.(), 0);
        }
        return next;
      });
    }, delay);
    return () => window.clearInterval(timer);
  }, [animate, isUser, rawContent]);

  return (
    <article className={`message-row ${isUser ? "message-row-user" : ""}`}>
      {!isUser && (
        <div className="assistant-avatar" aria-hidden="true">
          {botAvatar ? <img src={botAvatar} alt="" /> : <Zap size={22} />}
        </div>
      )}

      <div className={`message-bubble ${isUser ? "user-message" : "assistant-message"}`}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeKatex]}
          components={{
            a: ({ node, children, ...props }) => (
              <a {...props} target="_blank" rel="noreferrer">
                {children}
              </a>
            ),
            code: ({ node, inline, className, children, ...props }) =>
              inline ? (
                <code className="inline-code" {...props}>
                  {children}
                </code>
              ) : (
                <pre className="code-block">
                  <code className={className} {...props}>
                    {children}
                  </code>
                </pre>
              ),
          }}
        >
          {cleanContent}
        </ReactMarkdown>

        {mediaUrls.length > 0 && (
          <div className="media-grid">
            {mediaUrls.map((path) => {
              const url = getFullUrl(path);
              return (
                <div key={path} className="media-card">
                  <img src={url} alt="生成内容" />
                  <div className="media-actions">
                    <button type="button" onClick={() => window.open(url, "_blank")} aria-label="查看图片">
                      <Eye size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadMedia(url)}
                      aria-label="下载图片"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!isUser && cleanContent && (
          <button
            className="copy-message"
            type="button"
            onClick={() => navigator.clipboard.writeText(cleanContent)}
            aria-label="复制回复"
          >
            <Copy size={15} />
          </button>
        )}
      </div>
    </article>
  );
}
